import React, { useEffect, useState } from "react"
import { Box, Text } from "@chakra-ui/react"

import { Swiper, SwiperSlide } from "swiper/react"
import type SwiperCore from "swiper"
import { Autoplay, Navigation, Pagination } from "swiper"
import NextImage from "next/image"

import "swiper/swiper.min.css"
import { colours } from "@shared/ui.design-system"
// eslint-disable-next-line import/no-unresolved
import "swiper/css/navigation"
// eslint-disable-next-line import/no-unresolved
import "swiper/css/pagination"
// eslint-disable-next-line import/no-unresolved
import "swiper/css/thumbs"
import { VideoPlayer } from "components/VideoPlayer/VideoPlayer2"
import { isStorybook } from "utils/storybookUtils"
import { GenericMarkdown } from "components/Shop/PDPPage/GenericMarkdown"
import { dispatch } from "@shared/utils.events"
import { useInView } from "react-intersection-observer"
import type { VariantTypeForNewPDP } from "components/Shop/PDPPage/PDPBodyIndex/PDPBodyIndex"
import type { VideoAssetType, VisualBynderType } from "@shared/graphql.types"
import { HowTo } from "components/Shop/PDPPage/HowTo"
import { CustomPagination } from "./CustomPagination"

type Props = {
  variant: VariantTypeForNewPDP
}

const styles = {
  "--swiper-pagination-color": colours.accent.strong,
  ".swiper-slide": {
    marginBottom: "1.5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  ".swiper-slide > *": {
    flex: 1,
    marginBottom: "1.5rem",
  },
  img: {
    width: "100%",
  },
  ".swiper-pagination-bullet": {
    margin: "0 5px !important",
    width: "10px",
    height: "10px",
    backgroundColor: colours.secondary.strong,
    opacity: 1,
    cursor: "pointer",
  },
  ".swiper-pagination-bullet-active": {
    textDecoration: "none",
    backgroundColor: colours.accent.strong,
  },
  ".swiper-horizontal>.swiper-pagination-bullets, .swiper-pagination-bullets.swiper-pagination-horizontal, .swiper-pagination-custom, .swiper-pagination-fraction":
    { top: "auto", bottom: "20px" },
  ".swiper-button-next:after, .swiper-rtl .swiper-button-prev:after, .swiper-button-prev:after, .swiper-rtl .swiper-button-next:after":
    {
      color: colours.primary.medium,
      fontSize: "20px",
      position: "absolute",
      top: "-85px",
    },
}

export const HowToUse: React.FC<Props> = ({ variant }) => {
  const { howToUseSlides } = variant
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 1,
  })

  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (inView) {
      dispatch("page element viewed", {
        target: "PDP How to use",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  type SwiperInstance = SwiperCore
  const [howToSwiper, setHowToSwiper] = useState<SwiperInstance | null>(null)
  const handleBulletClick = (index: number) => {
    if (howToSwiper && howToSwiper.slideTo) {
      howToSwiper.slideTo(index)
    }
  }
  const hasHowToUseSlidesMedia = variant?.howToUseSlides?.filter(
    (slide) => slide?.imageOrVideo
  ).length

  return (
    <Box
      as="section"
      sx={styles}
      id="how-to-use"
      scrollMarginTop={{ base: "110px", lg: "150px" }}
      ref={ref}
    >
      <Text
        as="h3"
        variant="apercu"
        fontSize={{ base: "1.25rem", md: 9 }}
        fontWeight="medium"
        marginBottom={hasHowToUseSlidesMedia ? "1.5rem" : "1rem"}
      >
        How to use
      </Text>
      {hasHowToUseSlidesMedia ? (
        <>
          <Swiper
            loop
            modules={[Autoplay, Pagination, Navigation]}
            navigation
            pagination={{
              el: "#swiper-pagination-custom",
              clickable: true,
              bulletActiveClass: "swiper-pagination-bullet-active",
            }}
            onSlideChange={(swiper) => {
              dispatch("swiper.onslidechange", {
                target: "PDP How To",
                position: swiper?.realIndex,
              })
              setActiveIndex(swiper.realIndex)
            }}
            onSwiper={setHowToSwiper}
          >
            {howToUseSlides?.map((howToUseSlide, index) => {
              const description = howToUseSlide?.description
              const asset = howToUseSlide?.imageOrVideo as VisualBynderType

              const thumbnails = asset?.thumbnails
              const { sources, imageUrl, name } = asset as VideoAssetType

              const mappedSources = sources?.map((source) => {
                return {
                  src: source.uri,
                  type: source.contentType,
                }
              })

              return (
                <SwiperSlide key={description}>
                  <Box position="relative" w="full" h="full">
                    {thumbnails ? (
                      <NextImage
                        src={thumbnails?.size_OTF_BETA as string}
                        width="600"
                        height="600"
                        alt={asset?.name ?? ""}
                        unoptimized={isStorybook}
                        quality={85}
                        priority
                        style={{
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <VideoPlayer
                        options={{
                          responsive: true,
                          playsinline: true,
                          fluid: true,
                          sources: mappedSources,
                          aspectRatio: "1:1",
                          controls: true,
                        }}
                        videoPosition="PDP how to slides"
                        coverImageProps={{
                          src: imageUrl,
                          title: name,
                          alt: name,
                          priority: true,
                          quality: 85,
                          sizes: "(max-width: 1024px) 100vw, 50vw",
                          placeholder:
                            index === 0 || isStorybook ? "empty" : "blur",
                          blurDataURL: imageUrl,
                        }}
                        videoProps={{
                          title: asset?.name ?? "",
                          "aria-label": asset?.name ?? "",
                          id: "gallery-video-player",
                          className:
                            "video-js vjs-big-play-centered vjs-show-big-play-button-on-pause gallery-video gallery-video-zoom",
                        }}
                      />
                    )}
                    <CustomPagination
                      totalSlides={howToUseSlides.length}
                      activeIndex={activeIndex}
                      onBulletClick={handleBulletClick}
                    />
                  </Box>
                  {description && (
                    <GenericMarkdown
                      content={description}
                      wrapper="div"
                      forceWrapper
                    />
                  )}
                </SwiperSlide>
              )
            })}
          </Swiper>
        </>
      ) : (
        <HowTo variant={variant} />
      )}
    </Box>
  )
}
